import produce from "immer";
import { handleActions } from "redux-actions";

import { asDate } from "src/utils/date";

import { SaleState } from "../../interfaces";
import { METHOD_TYPE } from "../../enums";
import { LOG_OUT } from "../system/actions";
import {
  SET_EBAY_EXTRA_ROWS,
  SET_OLDEST_SALE_YEAR,
  SET_PENDING_UPLOADS,
  SET_UPLOAD_FILENAME,
  SET_UPLOADS_FROM_EBAY,
  SHIFT_PENDING_UPLOAD,
  UPDATE_PENDING_UPLOAD,
  PATCH_SALES_RECORDS,
} from "./actions";
import {
  START,
  FAIL,
  SUCCESS,
  UPLOAD_ITEMS,
  DELETE_ITEMS,
  MOVE_ITEM,
  FS_CREATE_ITEMS,
  FS_UPDATE_ITEMS,
  FS_DELETE_ITEMS,
  CREATE_ITEM,
  DELETE_ITEM,
  UPDATE_ITEM,
  RESET,
  SALE,
  RETURN_SALE,
  BUNDLED_SALES,
  CLEAR_UPDATE_METHOD,
  handleError,
  handleSuccess,
} from "../common";

function mapSaleFromFirestore(e: any) {
  const postedDate = asDate(e.posted_date);
  return {
    ...e,
    platforms_listed:
      typeof e.platforms_listed === "string"
        ? e.platforms_listed.split(",")
        : e.platforms_listed,
    purchase_date: asDate(e.purchase_date),
    list_date: asDate(e.list_date),
    sale_date: asDate(e.sale_date),
    return_date: asDate(e.return_date),
    ...(postedDate ? { posted_date: postedDate } : {}),
  };
}

const initialState: SaleState = {
  itemsById: {},
  total: 0,
  start: 1,
  end: 1,
  limit: 25,
  pendingUploads: [],
  uploadFilename: "",
  ebayExtraRows: [],
  ebayReturns: [],
  ebayUploads: false,
  oldestYear: null,

  method: METHOD_TYPE.LIST,
  loading: false,
  loaded: false,
  error: null,
  message: "",

  selected: [],
};

export const saleReducer = handleActions<SaleState, any>(
  {
    [SALE + RESET]: () => initialState,
    [SET_PENDING_UPLOADS]: (state, { payload }) => {
      const existing = Object.values(state.itemsById);
      return {
        ...state,
        pendingUploads: payload.filter((sale) => {
          if (!sale.sale_date) return false;
          if (sale.transaction_id) {
            if (existing.find((s) => s.transaction_id === sale.transaction_id))
              return false;
          }

          return true;
        }),
      };
    },
    [UPDATE_PENDING_UPLOAD]: (state, { payload }) =>
      produce(state, (draft) => {
        let index = -1;
        for (const key of ["uuid", "transaction_id", "item_title", "sku"]) {
          if (payload[key])
            index = state.pendingUploads.findIndex(
              (item) => payload[key] === item[key]
            );

          if (index >= 0) break;
        }

        if (index >= 0) draft.pendingUploads[index] = payload;
      }),
    [SHIFT_PENDING_UPLOAD]: (state, { payload }) =>
      produce(state, (draft) => {
        const firstIndex = state.pendingUploads.findIndex(
          (item) => item.unmatched
        );
        if (firstIndex >= 0) {
          draft.pendingUploads.splice(firstIndex, 1);
        }
      }),
    [SET_UPLOAD_FILENAME]: (state, { payload }) => ({
      ...state,
      uploadFilename: payload,
    }),
    [SALE + UPLOAD_ITEMS + START]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.UPLOAD;
        draft.loading = true;
        draft.loaded = false;
      }),
    [SALE + UPLOAD_ITEMS + SUCCESS]: handleSuccess,
    [SALE + UPLOAD_ITEMS + FAIL]: handleError,
    [SALE + DELETE_ITEMS + START]: (state) => ({
      ...state,
      method: METHOD_TYPE.DELETE_ALL,
      loaded: false,
      loading: true,
    }),
    [SALE + DELETE_ITEMS + SUCCESS]: (state, { payload }) => ({
      ...state,
      itemsById: {},
      loaded: true,
      loading: false,
    }),
    [SALE + DELETE_ITEMS + FAIL]: handleError,
    [BUNDLED_SALES + CREATE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.CREATE;
        draft.loading = true;
        draft.error = null;
      }),
    [RETURN_SALE + CREATE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.UPDATE;
        draft.loading = true;
        draft.error = null;
      }),
    [SALE + CREATE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.CREATE;
        draft.loading = true;
        draft.error = null;
      }),
    [SALE + CREATE_ITEM + SUCCESS]: handleSuccess,
    [SALE + CREATE_ITEM + FAIL]: handleError,
    [SALE + UPDATE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.UPDATE;
        draft.loading = true;
        draft.error = null;
      }),
    [SALE + UPDATE_ITEM + SUCCESS]: handleSuccess,
    [BUNDLED_SALES + UPDATE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.UPDATE;
        draft.loading = true;
        draft.error = null;
      }),
    [SALE + UPDATE_ITEM + FAIL]: handleError,
    [BUNDLED_SALES + MOVE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.MOVE;
        draft.loading = true;
        draft.error = null;
      }),
    [SALE + MOVE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.MOVE;
        draft.loading = true;
        draft.error = null;
      }),
    [SALE + MOVE_ITEM + SUCCESS]: handleSuccess,
    [SALE + MOVE_ITEM + FAIL]: handleError,
    [BUNDLED_SALES + DELETE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.DELETE;
        draft.loading = true;
        draft.error = null;
      }),
    [RETURN_SALE + DELETE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.DELETE;
        draft.loading = true;
        draft.error = null;
      }),
    [SALE + DELETE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.DELETE;
        draft.loading = true;
        draft.error = null;
      }),
    [SALE + DELETE_ITEM + SUCCESS]: handleSuccess,
    [SALE + DELETE_ITEM + FAIL]: handleError,
    [CLEAR_UPDATE_METHOD]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.LIST;
      }),
    [SET_UPLOADS_FROM_EBAY]: (state, { payload }) => ({
      ...state,
      ebayUploads: payload,
    }),
    [SET_EBAY_EXTRA_ROWS]: (state, { payload }) => ({
      ...state,
      ebayExtraRows: payload.extraRows,
      ebayReturns: payload.returns,
    }),
    [SET_OLDEST_SALE_YEAR]: (state, { payload }) => ({
      ...state,
      oldestYear: typeof payload === "number" ? payload : null,
    }),
    [PATCH_SALES_RECORDS]: (state, { payload }) =>
      produce(state, (draft) => {
        for (const patch of (payload || []) as Array<
          Partial<any> & { id: string }
        >) {
          if (!patch?.id) continue;
          const existing = draft.itemsById[patch.id];
          if (existing) draft.itemsById[patch.id] = { ...existing, ...patch };
        }
      }),
    [SALE + FS_CREATE_ITEMS]: (state, { payload }) =>
      produce(state, (draft) => {
        for (const e of payload as any[]) {
          const dateValue = asDate(e.sale_date)?.valueOf();
          if (!dateValue) {
            console.log({
              sale: e,
              saleDate: {
                seconds: e.sale_date,
                output: asDate(e.sale_date),
              },
            });
          }
          const mapped = mapSaleFromFirestore(e);
          if (mapped.id) draft.itemsById[mapped.id] = mapped;
        }
        draft.loading = false;
        draft.loaded = true;
      }),
    [SALE + FS_UPDATE_ITEMS]: (state, { payload }) =>
      produce(state, (draft) => {
        for (const sale of payload) {
          const mapped = mapSaleFromFirestore(sale);
          if (mapped.id) draft.itemsById[mapped.id] = mapped;
        }
      }),
    [SALE + FS_DELETE_ITEMS]: (state, { payload }) =>
      produce(state, (draft) => {
        for (const deleted of payload) {
          if (deleted?.id) delete draft.itemsById[deleted.id];
        }
      }),
    [LOG_OUT]: () => initialState,
  },
  initialState
);
